/**
 * The languages OKANE speaks, and which one this browser prefers.
 *
 * Plain JavaScript with no imports: the boot screens use it before the app —
 * or any database — exists (`src/boot.js`, reached by `test/boot.mjs` in Node).
 *
 * The preference is remembered per browser (`okane.language`). It is what the
 * sign-in screen speaks, and the language a ledger starts in when it has not
 * chosen one; changing the language in the app remembers it here too, so the
 * two never disagree. `intl` is what `Intl` is handed for dates and money:
 * Serbian is written in two scripts and the app uses the Latin one.
 */

export const LANGUAGES = [
  { code: 'en', label: 'English', intl: 'en-GB' },
  { code: 'sr', label: 'Srpski', intl: 'sr-Latn-RS' },
]

export const DEFAULT_LANGUAGE = 'en'

const KEY = 'okane.language'

const known = (code) => LANGUAGES.some((l) => l.code === code)

const storage = () => {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/** The language picked in this browser, or null. */
export function rememberedLanguage() {
  try {
    const code = storage()?.getItem(KEY)
    return known(code) ? code : null
  } catch {
    return null
  }
}

export function rememberLanguage(code) {
  try {
    if (known(code)) storage()?.setItem(KEY, code)
  } catch {
    // A browser that keeps nothing still gets the language for this page.
  }
}

/** The one picked here; else the browser's own, when OKANE speaks it; else English. */
export function preferredLanguage(nav = globalThis.navigator) {
  const remembered = rememberedLanguage()
  if (remembered) return remembered
  for (const tag of nav?.languages ?? [nav?.language]) {
    const code = String(tag ?? '').toLowerCase().split('-')[0]
    if (known(code)) return code
  }
  return DEFAULT_LANGUAGE
}
